
    import { Component, Vue, Emit, Prop, Watch } from 'vue-property-decorator';
    import { namespace, Getter } from "vuex-class";

    import VuePerfectScrollbar from 'vue-perfect-scrollbar';
    import draggable from 'vuedraggable';
    import vSelect from 'vue-select';
    import Multiselect from 'vue-multiselect';
    import { get } from "lodash";

    import { Guard, GuardType, PatrolZone, SetGuardTypePayload } from '@/store/dispatch/types';
    import { FeaturesList } from "@/store/types";
    import AddNewGuard from '@/components/dispatch/AddNewGuard.vue';
    import GuardTypeSelector from '@/components/dispatch/GuardTypeSelector.vue';
    import intersections from '@/services/intersections.service';
    import ConfirmationModal from "@/components-shared/ConfirmationModal.vue";
    import BulkEditDialog from '@/components/dispatch/BulkEditDialog.vue';
    import BulkUploadDialog from '@/components/dispatch/BulkUploadDialog.vue';
    import DispatchGuardTypes from "@/constants/DispatchGuardTypes";
    import GuardTypeFilter from "@/components/dispatch/GuardTypeFilter.vue";
    import vselect3 from "vselect3";
    import { AlarmQueueFilter } from '@/store/eventqueue/types';

    interface GuardWithZones extends Guard {
        zonesToAssign: PatrolZone[];
    }

    const Dispatch = namespace('dispatch');
    const STARTING_GUARD_TYPE_ID: number = 1;
    const Eventqueue = namespace("eventqueue");

    @Component({
        components: {
            draggable,
            'v-select': vSelect,
            vselect3,
            'vue-perfect-scrollbar': VuePerfectScrollbar,
            'add-new-guard': AddNewGuard,
            'multiselect': Multiselect,
            GuardTypeSelector,
            "confirmation-modal": ConfirmationModal,
            "bulk-edit-dialog": BulkEditDialog,
            "bulk-upload-dialog": BulkUploadDialog,
            "guard-type-filter": GuardTypeFilter,
        }
    })

    export default class GuardsCollection extends Vue {
        @Dispatch.State (state => state.BulkEditDialogOpen) bulkEditDialogOpen: boolean;
        @Dispatch.State (state => state.BulkUploadDialogOpen) bulkUploadDialogOpen: boolean;
        
        @Dispatch.Getter('getGuardsList') guards!: Guard[];
        @Dispatch.Getter getPatrolZoneByID: (id: number) => PatrolZone;
        @Dispatch.Getter getGuardByID: (id: number) => Guard;
        @Dispatch.Getter('getGuardTypes') guardTypes!: GuardType[];
        @Dispatch.Action loadGuards: () => Promise<void>;
        @Dispatch.Action addNewGuard: (guard: Guard) => Promise<void>;
        @Dispatch.Action updateGuard: (guard: Guard) => Promise<void>;
        @Dispatch.Action deleteGuard: (guard: Guard) => Promise<void>;
        @Dispatch.Action filterGuardListByCallsign: (filterPhrase: string) => Promise<void>;
        @Dispatch.Action filterGuardListByPatrolZone: (patrolZoneID: number) => Promise<void>;
        @Dispatch.Action loadGuardTypes: () => Promise<void>;
        
        @Dispatch.Mutation setGuardType:(data: SetGuardTypePayload) => void;
        @Dispatch.Mutation setBulkEditDialogOpen: (open: boolean) => void;
        @Dispatch.Mutation setBulkUploadDialogOpen: (open: boolean) => void;

        @Eventqueue.State (state => state.AlarmQueueFilters.data) alarmQueueFilter!: AlarmQueueFilter[];
        @Eventqueue.Action public loadAlarmQueueFiltersForAll;

        @Getter("getFeaturesList") featuresList: FeaturesList;

        @Prop({
            required: true,
            default: () => ([])
        })
        patrolZones!: PatrolZone[];

        get filterPatrolZones(): PatrolZone[] {
            return [
                { patrolZoneID: 0, title: 'Unassigned' }, ...this.patrolZones
            ];
        }

        @Prop({
            required: true,
            default: () => ([])
        })
        renderedZones!: any[];

        public listFilter: string = '';
        public isGuardEditing: boolean = false;
        public isDragging: boolean = false;
        public addingNewGuard: boolean = false;
        public newGuard: Guard = {
            callsign: '',
            patrolZones: {},
            dispatchGuardTypeID: STARTING_GUARD_TYPE_ID
        };
        public editedGuard: GuardWithZones | null = null;
        public editedGuardOrigGuardTypeID: number | null;
        public guardTypesDictionary: any = {};
        public hasValidationErrors: boolean = false;
        public showErrorAnimation: boolean = false;
        // https://github.com/SortableJS/Sortable#options
        public dragOptions = {
            animation: 0,
            group: {
                name,
                pull: 'clone',
                put: false
            },
            sort: false,
            ghostClass: 'ghost'
        };

        public selectedDispatchGuardTypeID?: number = null;

        private guardToDelete: Guard = null;
        private showDeleteModal: boolean = false;

        private selectedGuards: Guard[] = [];
        private allGuardsSelected: boolean = false;
        private alarmQueueEdit: AlarmQueueFilter = null;
        private filterValue: PatrolZone = null;
        private dispatchGuardTypes = DispatchGuardTypes;
        private isLoadingAlarmQueueFilterItems: boolean = false;
        private selectedAlarmQueueFilter: AlarmQueueFilter | null = null;

        public get assignedZonesList() {
            return (guard: Guard) => {
                return Object.keys(guard.patrolZones).map(key => {
                    return { patrolZoneID: Number(key), title: guard.patrolZones[key] };
                });
            }
        }
        //Sets the assign alarm to the editing guard
        private assignedAlarmQueue(guard: Guard) {
            return this.alarmQueueFilter.filter(
                (alarm: AlarmQueueFilter) => alarm.alarmQueueFilterID === guard.alarmQueueFilterID
            )[0];
        }

        public get assignedZones() {
            return (guard: GuardWithZones) => {
                return this.assignedZonesList(guard).map(zone => zone.title).join(', ');
            };
        }

        public get zonesToAssign() {
			return (guard: GuardWithZones) => {
				return this.guardAssignableZones(guard).filter((patrolZone: PatrolZone) => {
                    return !this.assignedZonesList(guard)
                        .filter(zone => zone.patrolZoneID === patrolZone.patrolZoneID).length
                });
            };
        }

        public get getRenderedZone() {
            return (id: number) => this.renderedZones.filter(
                (zone: any) => zone.id === id
            )[0];
        }

		guardAssignableZones(guard: any) {
			const assignedZonesIDs = this.assignedZonesList(guard).map(zone => zone.patrolZoneID);
			let zonesToAssignIDs: number[] = [];
			let zonesToAssign: PatrolZone[] = [];

			if (assignedZonesIDs.length) {
				// put ids of previously assigned zones at the beginning of the array
				zonesToAssignIDs = [...this.patrolZones]
					.filter(zone => !assignedZonesIDs.includes(zone.patrolZoneID))
					.map(zone => zone.patrolZoneID);
				zonesToAssignIDs = [
					...assignedZonesIDs,
					...zonesToAssignIDs
				];

				// get zones' polygons and check intersections
				const renderedZonesToAssign = zonesToAssignIDs
					.map(id => this.getRenderedZone(id))
					.filter(zone => zone);
				const zones = renderedZonesToAssign.map(zone => zone.zone);
				const target = intersections.getZonesIntersection(zones);

				if (target.zones.length) {
					// if we have the interserction - store all intersected zones
					// as a list of zones available for assignment

					renderedZonesToAssign.forEach(zone => {
						const isZoneInIntersection = !!target.zones.filter(polygon => polygon === zone.zone).length;

						isZoneInIntersection && zonesToAssign.push(this.getPatrolZoneByID(zone.id));
					});
				}
			}

			return zonesToAssign;
		}

		public get guardsListWithZoneSuggestions(): Guard[] {
			return this.guards;
        }

        public get showEditControls() {
            return (guard: GuardWithZones) => this.isGuardEditing && guard === this.editedGuard;
        }

        async mounted() {
            await this.loadGuards();
            this.getAlarmQueueFilters();
            await this.loadGuardTypes();
            this.guardTypes.forEach((type: GuardType) => {
                this.guardTypesDictionary[type.dispatchGuardTypeID] = type;
            });
            this.selectedGuards = [];
        }

        public onAddNewGuard() {
            const guard: Guard = { ...this.newGuard };
            if(this.filterValue != null) {
                guard.patrolZones[this.filterValue.patrolZoneID] =  this.filterValue.title ;
            }
            this.addingNewGuard = false;
            this.newGuard.callsign = '';
            this.newGuard.patrolZones = {};

            guard.dispatchGuardTypeID = DispatchGuardTypes.Roaming;
            this.addNewGuard(guard);
        }

        @Emit('deleteGuard')
        public async onDeleteGuard() {
            let guardID = this.guardToDelete.guardID;
            this.showDeleteModal = false;
            await this.deleteGuard(this.guardToDelete);
            this.guardToDelete = null;
            return guardID;
        }

        public onToggleUpdateGuard(guard: GuardWithZones, shouldUpdate: boolean, onEditClick: boolean = false) {   
            if (onEditClick) { 
                this.selectedAlarmQueueFilter = this.assignedAlarmQueue(guard);
            }

            if (this.editedGuard !== guard) {
                this.isGuardEditing = false;
                this.editedGuard = null;
            }

            if (this.isGuardEditing) {
                if (!guard || !guard.callsign || guard.callsign.length == 0) {
                    this.showGuardEditValidationError();
                    return;
                }

                if (shouldUpdate) {
                    if (this.selectedAlarmQueueFilter === null || this.selectedAlarmQueueFilter === undefined) {
                        this.selectedAlarmQueueFilter = {
                            alarmQueueFilterID: null,
                            title: "",
                            regionalFilterID: "",
                            regionID: null
                        };
                    }

                    const updatedGuard = {...guard, 
                                        alarmQueueFilterID: this.selectedAlarmQueueFilter.alarmQueueFilterID,
                                        alarmQueueFilterTitle: this.selectedAlarmQueueFilter.title}

                    this.updateGuard(updatedGuard);
                } else {
                    // On the case the user cancels, set the guard type back to original
                    this.setGuardType({
                        guardID: this.editedGuard.guardID, 
                        dispatchGuardTypeID: this.editedGuardOrigGuardTypeID
                    });
                }

                this.editedGuard = null;
                this.editedGuardOrigGuardTypeID = null;
                this.hasValidationErrors = false;
                this.alarmQueueEdit = null;

            } else {
                this.editedGuard = guard;
                this.editedGuardOrigGuardTypeID = guard.dispatchGuardTypeID;
            }

            this.isGuardEditing = !this.isGuardEditing;
        }

        public showGuardEditValidationError() {
            this.hasValidationErrors = true;
            this.showErrorAnimation = true;

            setTimeout(() => {
                this.showErrorAnimation = false;
            }, this.$config.ANIMATION_DURATION);
        }

        public async getAlarmQueueFilters()
        {
            this.isLoadingAlarmQueueFilterItems = true;

            await this.loadAlarmQueueFiltersForAll();

            this.isLoadingAlarmQueueFilterItems = false;
        }

        public onAssignedZoneChange(zones: any) {
            const guardID = this.editedGuard!.guardID;
            const guardAssignedZones = Object.keys(this.editedGuard!.patrolZones);
            const zoneIDs = zones.map((zone: PatrolZone) => zone.patrolZoneID);

            if (zones.length === guardAssignedZones.length) {
                //initial assignment
                return;
            }

            if (guardID) {
                this.$emit('assignedZoneChange', {
                    guardID,
                    zoneIDs
                });
            }
        }

        @Emit('dragEnd')
        public onDragEnd(event: any): void {
            this.isDragging = false;
        }

        @Emit('dragStart')
        public onDragStart(event: any) : Guard {
            this.isDragging = true;
            return this.guards[event.oldIndex];
        }

        onCallSignListFilter(ev): void {
            this.filterGuardListByCallsign(ev.target.value);
        }

        onPatrolZoneListFilter(value: PatrolZone): void {
            this.filterValue = value ? value : null;
            this.filterGuardListByPatrolZone(value? this.filterValue.patrolZoneID: -1);
        }

        //retrieves the alarm filters for the edit dropdown
        public get alarmQueueFilters(): AlarmQueueFilter[] {
			return this.alarmQueueFilter;
        }

        //sets the new alarm queue filter for the currently edited guard
        onAlarmQueueFilterChange(): void {
            if(this.alarmQueueEdit === null)
                this.alarmQueueEdit = {title:"",alarmQueueFilterID:null,regionalFilterID:"",regionID:null};
            else
                this.alarmQueueEdit = {title:this.alarmQueueEdit.title,alarmQueueFilterID:this.alarmQueueEdit.alarmQueueFilterID,regionalFilterID:"",regionID:null};
        }

        public async updateGuardType(guard: Guard, guardTypeId: DispatchGuardTypes) {
            this.setGuardType({
                guardID: guard.guardID, 
                dispatchGuardTypeID: guardTypeId
            });
            guard.dispatchGuardTypeID = guardTypeId;
            await this.updateGuard(guard);
        }

        private toggleDeleteGuard(guard: Guard): void {
            this.guardToDelete = guard;
            if(guard)
                this.showDeleteModal = true;
            else
                this.showDeleteModal = false;        
        }

        // Handles the toggling of a check box row
        private guardSelectedToggled(event):void {
            if (event.target.checked) {
                // Row Selected
                let selectedGuard:Guard = this.guardsListWithZoneSuggestions.filter(g => g.guardID == event.target.value)[0];
                this.selectedGuards.push(selectedGuard);

                //  Need to check if checking this row now means allGuardsSelected should now be true
                if (this.selectedGuards.length === this.guardsListWithZoneSuggestions.length) {
                     this.allGuardsSelected = true;
                }
            } else {
                // Row Deselected
                const guardIndex: number = this.selectedGuards.findIndex(g => g.guardID == event.target.value);
                this.selectedGuards.splice(guardIndex,1);
                
                // A row has been deselected so 'All rows can no longer be selected'
                this.allGuardsSelected = false;
            }
        }

        // Checks if the CheckBox for the row should be selected (for bulk edit)
        private isGuardSelected(guardID: number) : boolean {
            return this.selectedGuards.some(g => g.guardID == guardID);
        }

        // Opens the Bulk Edit dialog (by updating the state)
        private openBulkEditDialog(): void {
			this.setBulkEditDialogOpen(true);
        }

         // Opens the Bulk Upload dialog (by updating the state)
        private openBulkUploadDialog(): void {
			this.setBulkUploadDialogOpen(true);
        }
        
        // Checks if the feature for 'Bulk Edit' is set
        private get isbulkEditGuardsEnabled(): boolean {
            return get(this.featuresList, ["Dispatch", "BulkUpdateGuards"], false);
        }

        // Checks if the feature for 'Bulk Add' (Upload) is set
        private get isbulkAddGuardsEnabled(): boolean {
            return get(this.featuresList, ["Dispatch", "BulkAddGuards"], false);
        }

        // Reloads the guards - call when event emitted by close dialog
        private refresh(): void {
            this.loadGuards();
        }

        // Handles the toggling of the Select All check box
        public selectAllToggled(checked): void {
            if (checked) {
                // 'Select All' checked
                this.selectedGuards = [...this.guardsListWithZoneSuggestions];
            } else {
                 // 'Select All' unchecked
                 this.selectedGuards = [];
            }
        }

        public getSortedGuardIds(toSort:Guard[]) : number[] {
            return toSort.map(g => g.guardID).sort();
        }

        @Watch("guardsListWithZoneSuggestions.length")
        public checkAllRowsSelected() {
            if (this.selectedGuards.length > 0) {
                // find if anything in the selectGuards list no longer exists in the main list of guards
                const deletedGuards:Guard[] = this.selectedGuards.filter(g1 => !this.guardsListWithZoneSuggestions.some(g2 => g1.guardID == g2.guardID));
                if (deletedGuards.length > 0) {
                    deletedGuards.forEach((guard:Guard) => {
                        this.selectedGuards = this.selectedGuards.filter(g => g.guardID !== guard.guardID);
                    });

                    // if the number of selected now matches the number of guards set the all selected 
                    if (this.selectedGuards.length === this.guardsListWithZoneSuggestions.length) {
                        this.allGuardsSelected = true;
                    }
                }
                else {
                    // A selected guard wasn't deleted, so either a guard added or a non selected guard deleted
                    // Nothing delete so a guard must have been added
                    // Check if the arrays match by creating a sorted arrays of guard Id's
                    let allGuardIds: number[] = this.getSortedGuardIds(this.guardsListWithZoneSuggestions);
                    let selectedGuardIds: number[] = this.getSortedGuardIds(this.selectedGuards);
                    
                    if (allGuardIds.toString().localeCompare(selectedGuardIds.toString()) == 0) {
                        this.allGuardsSelected = true;
                    } else {
                        this.allGuardsSelected = false;
                    }  
                }  
            } 
        }

        public locatePatrolZone(patrolZoneID: number) {
            this.$emit("locatePatrolZone", patrolZoneID);
        }
    }
